import type { RGB } from '../types.ts';
import { sampleRamp } from './ramp.ts';

/** Heuristic: does the polyline end where it started (within ~2 median edges)? */
export function isNearClosedXY(f32: Float32Array, nPoints: number, D: number): boolean {
  if (!f32 || nPoints < 3) return false;
  const x0 = f32[0], y0 = f32[1];
  const j1 = (nPoints - 1) * D;
  const d = Math.hypot(f32[j1] - x0, f32[j1 + 1] - y0);
  if (d <= 1e-9) return true;
  const segs = nPoints - 1;
  const sampleN = Math.min(2048, segs);
  if (sampleN <= 0) return false;
  const lens = new Array<number>(sampleN);
  for (let k = 0; k < sampleN; k++) {
    const j = Math.floor((k * segs) / sampleN) * D;
    lens[k] = Math.hypot(f32[j + D] - f32[j], f32[j + D + 1] - f32[j + 1]);
  }
  lens.sort((a, b) => a - b);
  const med = lens[Math.floor(sampleN / 2)] || 0;
  return med > 0 && d <= 2.0 * med;
}

/** Seam (max channel jump between ramp ends) and muddy-purple sample count. */
export function rampDiagnostics(stops: RGB[], N = 1200): { seam: number; muddy: number; N: number } {
  const a = sampleRamp(stops, 0), b = sampleRamp(stops, 1);
  const seam = Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));
  let muddy = 0;
  for (let i = 0; i < N; i++) {
    const c = sampleRamp(stops, i / N);
    if (c[1] < Math.min(c[0], c[2]) - 0.5) muddy++;
  }
  return { seam, muddy, N };
}
